import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { AvisoEnPantalla } from './componentes/AvisoEnPantalla';
import { Layout } from './componentes/Layout';
import { SinConexion } from './componentes/SinConexion';
import { useSesion } from './lib/sesion';
import { CasetaPagina } from './rutas/caseta';
import { ValidarPagina } from './rutas/validar';
import { LecturasPagina } from './rutas/lecturas';
import { Login } from './rutas/login';
import { ResidentePagina } from './rutas/residente';
import { PadronPagina } from './rutas/padron';
import { UnidadPagina } from './rutas/unidad';
import { HistorialPagina } from './rutas/historial';
import { CondominioPagina } from './rutas/condominio';
import { aDondeMandar } from './lib/guardian';
import { ContrasenaPagina } from './rutas/contrasena';
import { FraccionamientoPagina } from './rutas/fraccionamiento';
import { PanelInicio } from './rutas/panel';
import { ServidorPagina } from './rutas/servidor';
import { TelefoniaPagina } from './rutas/telefonia';
import { AvisosPagina } from './rutas/avisos';
import { PrivacidadPagina } from './rutas/privacidad';
import { RecuperarPagina } from './rutas/recuperar';
import { TiposVisitaPagina } from './rutas/tipos-visita';
import { AdentroPagina } from './rutas/adentro';
import { TopologiaPagina } from './rutas/topologia';
import { UsuariosPagina } from './rutas/usuarios';

function Protegida({ children }: { children: ReactNode }) {
  const { usuario, cargando, sinConexion, reintentar } = useSesion();
  const { pathname } = useLocation();

  // Sin este estado intermedio, recargar la pestaña con sesión válida rebota
  // un instante al login mientras se revalida el token. En una tablet lenta
  // ese parpadeo se ve, y parece que el sistema se cayó.
  if (cargando) {
    return (
      <div className="flex min-h-full items-center justify-center text-kodica-texto-tenue">
        Cargando…
      </div>
    );
  }

  // **Sin conexión no es lo mismo que sin sesión.** El token sigue guardado;
  // lo que faltó fue con quién confirmarlo. Mandar aquí al login haría que una
  // caída del servidor se viera igual que «te sacamos», y el residente
  // tecleara su contraseña para recibir un error de red.
  if (sinConexion) return <SinConexion alReintentar={reintentar} />;

  if (!usuario) return <Navigate to="/login" replace />;

  // Las dos condiciones que hay que resolver antes de dejar usar la app viven
  // juntas en `aDondeMandar`, y no como guardias sueltos aquí. La razón está
  // escrita ahí: sueltos se mandaban el uno al otro y dejaban la pantalla en
  // blanco.
  const desvio = aDondeMandar(usuario, pathname);
  if (desvio) return <Navigate to={desvio} replace />;

  return <>{children}</>;
}

/**
 * Una pantalla que solo existe donde hay reja.
 *
 * **Ocultarla del menú no basta.** La dirección sigue en el historial, en un
 * marcador y en la pestaña que alguien dejó abierta, y el router de React la
 * atiende igual. En la nube eso pintaba la pantalla de caseta contra un API que
 * no tiene esas rutas: «Cannot GET /api/accesos/pendientes» —y peor, el sondeo
 * de visitas pendientes reintentando contra un 404 en bucle, más la reconexión
 * eterna del canal de eventos—.
 *
 * Con esto la dirección deja de existir también del lado del navegador, que es
 * lo que la hace equivalente a no estar.
 */
function SoloCaseta({ children }: { children: ReactNode }) {
  const { rolServidor } = useSesion();
  if (rolServidor === 'nube') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Un residente no opera el condominio: invita a su casa y ve lo suyo. Se le
 * manda a su propia vista, que además está pensada para teléfono. Sin esto
 * vería el panel del guardia con secciones que el API le va a negar.
 */
function SegunRol() {
  const { usuario } = useSesion();
  if (usuario?.rol === 'RESIDENTE') return <Navigate to="/mi-vivienda" replace />;
  // El guardia entra directo a su pantalla. El panel le enseñaba contadores de
  // inventario —cuántas unidades, cuántos residentes— que no le sirven de nada
  // en su turno, y le costaba un toque de más llegar a lo único que usa, con
  // alguien esperando en la reja.
  if (usuario?.rol === 'GUARDIA') return <Navigate to="/caseta" replace />;
  return <PanelInicio />;
}

export function App() {
  return (
    <>
      {/* Fuera de las rutas: un aviso que llega no debe depender de en qué
          pantalla esté el residente. */}
      <AvisoEnPantalla />
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* Sin sesión, como el login: quien la necesita es justo quien no puede
            entrar. */}
        <Route path="/recuperar" element={<RecuperarPagina />} />

        {/* Sin sesión a propósito: se cambia de servidor justo cuando no se
          puede entrar, que es cuando la dirección suele estar mal. */}
        <Route path="/servidor" element={<ServidorPagina />} />

        {/* **Pública y sin sesión, y eso es un requisito, no una comodidad.**
            Google Play y App Store exigen una dirección del aviso de privacidad
            que su revisor pueda abrir sin instalar la app ni tener cuenta. Si
            algún día esta ruta cae detrás del guardián, las dos tiendas
            rechazan la siguiente versión. */}
        <Route path="/privacidad" element={<PrivacidadPagina />} />

        {/* Fuera del Layout: la usan por igual el guardia desde la tablet y el
          residente desde su teléfono, y ese no tiene barra lateral. */}
        <Route
          path="/contrasena"
          element={
            <Protegida>
              <ContrasenaPagina />
            </Protegida>
          }
        />

        {/* Fuera del Layout a propósito: el residente entra desde el celular y
          una barra lateral de escritorio ahí no tiene sentido.

          Con `/*` porque cada sección del residente es una dirección propia:
          eso es lo que le da historial al botón atrás de Android, que si no
          cierra la app desde cualquier pantalla. */}
        <Route
          path="/mi-vivienda/*"
          element={
            <Protegida>
              <ResidentePagina />
            </Protegida>
          }
        />

        <Route
          element={
            <Protegida>
              <Layout />
            </Protegida>
          }
        >
          <Route path="/" element={<SegunRol />} />
          <Route
            path="/caseta"
            element={
              <SoloCaseta>
                <CasetaPagina />
              </SoloCaseta>
            }
          />
          <Route
            path="/validar"
            element={
              <SoloCaseta>
                <ValidarPagina />
              </SoloCaseta>
            }
          />
          <Route
            path="/lecturas"
            element={
              <SoloCaseta>
                <LecturasPagina />
              </SoloCaseta>
            }
          />
          <Route
            path="/adentro"
            element={
              <SoloCaseta>
                <AdentroPagina />
              </SoloCaseta>
            }
          />
          <Route path="/padron" element={<PadronPagina />} />
          <Route path="/padron/unidad/:id" element={<UnidadPagina />} />
          <Route path="/condominio" element={<CondominioPagina />} />
          <Route path="/fraccionamiento" element={<FraccionamientoPagina />} />
          <Route path="/usuarios" element={<UsuariosPagina />} />
          {/* Las pestañas van en la dirección: esta pantalla se abre para
              responderle a alguien, y hay que poder mandar el enlace. */}
          <Route path="/historial" element={<Navigate to="/historial/ingresos" replace />} />
          <Route path="/historial/:pestana" element={<HistorialPagina />} />
          {/* Con comodín: la pantalla lleva dentro su propio recorrido
              —accesos, carriles, aparatos, ficha— y cada nivel es una ruta,
              para que el botón de atrás funcione y se pueda mandar el enlace
              de un aparato a quien lo está instalando. */}
          <Route path="/topologia/*" element={<TopologiaPagina />} />
          <Route path="/avisos" element={<AvisosPagina />} />
          <Route path="/tipos-visita" element={<TiposVisitaPagina />} />
          <Route
            path="/telefonia"
            element={
              <SoloCaseta>
                <TelefoniaPagina />
              </SoloCaseta>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
